import { createContext, useContext } from "react";
import { UserOrganization } from "./useUserOrganizations";

/** The active tenant, the switch list, and the `switchOrg` action. */
export interface TenantContextValue {
  /**
   * The active tenant (Auth0 organization) resolved from the access token's
   * `org_id` claim, falling back to the persisted selection. `null` while
   * loading or for a not-yet-scoped personal login.
   */
  currentOrg: UserOrganization | null;
  /** The organizations the user may switch between. Empty while loading. */
  organizations: UserOrganization[];
  /** True while the organization list is being fetched. */
  isLoading: boolean;
  /**
   * True from when a `switchOrg` call launches the org-scoped redirect until
   * the resulting full-page navigation unmounts the app.
   */
  isSwitching: boolean;
  /**
   * Switch the active tenant via a full-page, org-scoped `loginWithRedirect`
   * — NOT a silent token re-mint (see `TenantProvider` for why silent is wrong
   * here). Persists the selection, then navigates: the app re-mounts scoped to
   * the new org, which re-scopes every token and drops in-memory React Query
   * state. On success it never resolves (the page navigates away); it rejects
   * only if the redirect can't be launched. Membership is enforced by Auth0
   * server-side during the redirect, not here.
   */
  switchOrg: (orgId: string) => Promise<void>;
}

export const TenantContext = createContext<TenantContextValue | null>(null);

/** Access the active tenant, switch list, and `switchOrg` action. */
export function useTenantContext(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenantContext must be used inside a TenantProvider");
  }
  return context;
}
